import type { Action } from 'state/actions';
import * as addonActionTypes from 'state/addonForm/addonForm.actionTypes';
import * as addonManagementActionTypes from 'state/addonManagementForm/addonManagementForm.actionTypes';
import * as batchActionTypes from 'state/batchForm/batchForm.actionTypes';
import * as dynamicActionTypes from 'state/dynamic/dynamicForm.actionTypes';
import { addKey, addOrPatchKey, patchKey, removeKey, removeKeys } from 'state/utils/collection';

import type { QuoteResult, QuoteResultsMap } from './result.model';

export function quoteResultsReducer(state: QuoteResultsMap = {}, action: Action): QuoteResultsMap {
  switch (action.type) {
    case 'QUOTE_ACKED':
    case 'STREAMING_QUOTE_ACKED': {
      const {
        quoteId,
        externalQuoteId: quoteExternalId,
        quoteType,
        quoteRequestStartingTimeInMillis,
        estimatedQuotingDurationInMillis,
        isExecOnlyAddon,
      } = action;

      return addKey(state, quoteId, {
        quoteId,
        quoteExternalId,
        quoteType,
        quoteRequestStartingTimeInMillis,
        estimatedQuotingDurationInMillis,
        isExecOnlyAddon,
      } as QuoteResult);
    }

    case addonActionTypes.ADDON_QUOTE_ACKED: {
      const {
        quoteId,
        externalQuoteId: quoteExternalId,
        quoteType,
        quoteRequestStartingTimeInMillis,
        estimatedQuotingDurationInMillis,
        isExecOnlyAddon,
      } = action;

      return addKey(state, quoteId, {
        quoteId,
        quoteExternalId,
        quoteType,
        quoteRequestStartingTimeInMillis,
        estimatedQuotingDurationInMillis,
        isExecOnlyAddon,
      } as QuoteResult);
    }

    case dynamicActionTypes.DYNAMIC_BOOKING_ACKED: {
      const { quoteId, quoteExternalId, negotiationId } = action;

      return addKey(state, quoteId, <QuoteResult>{
        quoteId,
        quoteExternalId,
        negotiationId,
      });
    }

    case batchActionTypes.BATCH_QUOTE_ACKED:
      return action.forms.reduce(
        (result, form) =>
          addKey(result, form.quoteId, <QuoteResult>{
            quoteId: form.quoteId,
            negotiationId: form.negotiationId,
            quoteExternalId: form.quoteExternalId,
            quoteRequestStartingTimeInMillis: action.quoteRequestStartingTimeInMillis,
            estimatedQuotingDurationInMillis: form.pricingEstimatedDurationInMilliseconds,
            isExecOnlyAddon: form.isExecOnlyAddon,
          }),
        state,
      );
    case addonManagementActionTypes.ADDON_MANAGEMENT_QUOTE_ACKED:
      return action.forms.reduce(
        (result, form) =>
          addKey(result, form.quoteId, <QuoteResult>{
            quoteId: form.quoteId,
            quoteExternalId: form.quoteExternalId,
            estimatedQuotingDurationInMillis: form.pricingEstimatedDurationInMilliseconds,
          }),
        state,
      );

    case 'PREVIOUS_QUOTE_LOADED':
    case batchActionTypes.BATCH_PREVIOUS_QUOTE_LOADED:
      if (state[action.quoteId] !== undefined) {
        return state;
      }
      return addKey(state, action.quoteId, <QuoteResult>{
        ...action.quote,
        quoteDetails: undefined,
        technicalError: undefined,
        estimatedQuotingDurationInMillis: undefined,
        quoteRequestStartingTimeInMillis: undefined,
      });

    case 'QUOTE_RECEIVED':
      const {
        quoteId,
        quote: {
          negotiationId,
          productSubType,
          quoteExternalId,
          priceEnhancement,
          pricingTime,
          rejections,
          solveResult,
          solveResultFormat,
          status,
          createdAt,
          availableActivities,
          warnings,
          tradeWay,
          tradeDirection,
          tradeExecutionWindowInMilliseconds,
        },
      } = action;
      return addOrPatchKey(state, quoteId, state => ({
        quoteId,
        negotiationId,
        quoteExternalId: quoteExternalId ?? state.quoteExternalId,
        productSubType,
        createdAt,
        pricingTime,
        priceEnhancement,
        rejections,
        solveResult,
        solveResultFormat,
        status,
        estimatedQuotingDurationInMillis: undefined,
        quoteDetails: undefined,
        technicalError: undefined,
        quoteRequestStartingTimeInMillis: undefined,
        availableActivities,
        warnings,
        tradeWay,
        tradeDirection,
        tradeExecutionWindowInMilliseconds: tradeExecutionWindowInMilliseconds ?? 0,
      }));

    case batchActionTypes.BATCH_QUOTE_RECEIVED:
    case addonActionTypes.ADDON_QUOTE_RECEIVED:
    case addonManagementActionTypes.ADDON_MANAGEMENT_QUOTE_RECEIVED:
      return patchKey(state, action.quoteId, () => ({
        ...action.quote,
        quoteDetails: undefined,
        technicalError: undefined,
        estimatedQuotingDurationInMillis: undefined,
        quoteRequestStartingTimeInMillis: undefined,
      }));

    case 'QUOTE_RESULT_FAILED':
    case addonActionTypes.ADDON_QUOTE_RESULT_FAILED:
    case addonManagementActionTypes.ADDON_MANAGEMENT_QUOTE_RESULT_FAILED:
      return patchKey(state, action.quoteId, () => ({
        technicalError: action.technicalError,
        estimatedQuotingDurationInMillis: undefined,
        quoteRequestStartingTimeInMillis: undefined,
      }));
    case batchActionTypes.BATCH_QUOTE_RESULT_FAILED:
    case addonManagementActionTypes.ADDON_MANAGEMENT_QUOTE_EXPIRED:
    case addonActionTypes.ADDON_QUOTE_REMOVED:
      return removeKey(state, action.quoteId);

    case 'QUOTES_REMOVED':
      return removeKeys(state, action.quoteIds);

    default:
      return state;
  }
}
